import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { RootState } from '../store/store';
import { UserRole } from '../types';
import { poolMembersService, PoolMember } from '../services/poolMembersService';
import {
  poolMemberPaymentsService,
  PoolMemberPaymentSummary,
  PoolMemberPayment,
} from '../services/poolMemberPaymentsService';

export const AdminPoolMemberPaymentsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [members, setMembers] = useState<PoolMember[]>([]);
  const [summary, setSummary] = useState<PoolMemberPaymentSummary[]>([]);
  const [payments, setPayments] = useState<PoolMemberPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [startDate, setStartDate] = useState(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  const [showAddModal, setShowAddModal] = useState(false);
  const [activePoolMemberId, setActivePoolMemberId] = useState<string>('');
  const [unpaidTransfers, setUnpaidTransfers] = useState<any[]>([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [selectedTransferForPayment, setSelectedTransferForPayment] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyMemberId, setHistoryMemberId] = useState('');

  const canManage = user?.role === UserRole.ORG_ADMIN || user?.role === UserRole.MANAGER || user?.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => { loadMembers(); }, []);
  useEffect(() => { loadData(); }, [selectedMemberId, startDate, endDate]);

  const loadMembers = async () => {
    try {
      const data = await poolMembersService.getAll();
      setMembers(data);
    } catch { toast.error('Üyeler yüklenemedi'); }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryData, paymentsData] = await Promise.all([
        poolMemberPaymentsService.getSummary({ poolMemberId: selectedMemberId || undefined, startDate, endDate }),
        poolMemberPaymentsService.getAll({ poolMemberId: selectedMemberId || undefined, startDate, endDate }),
      ]);
      setSummary(summaryData);
      setPayments(paymentsData);
    } catch { toast.error('Veriler yüklenemedi'); }
    finally { setLoading(false); }
  };

  const openAddModal = async (memberId: string) => {
    setActivePoolMemberId(memberId);
    setPaymentAmount('');
    setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMethod('cash');
    setPaymentNotes('');
    setSelectedTransferForPayment('');
    try {
      const transfers = await poolMemberPaymentsService.getUnpaidTransfers(memberId);
      setUnpaidTransfers(transfers);
    } catch { setUnpaidTransfers([]); }
    setShowAddModal(true);
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) { toast.error('Geçerli bir tutar girin'); return; }
    try {
      await poolMemberPaymentsService.create({
        poolMemberId: activePoolMemberId,
        transferId: selectedTransferForPayment || undefined,
        amount: parseFloat(paymentAmount),
        paymentDate,
        paymentMethod,
        notes: paymentNotes || undefined,
      });
      toast.success('Ödeme kaydedildi');
      setShowAddModal(false);
      loadData();
    } catch (err: any) { toast.error(err.response?.data?.message || 'Ödeme kaydedilemedi'); }
  };

  const handleDeletePayment = async (id: string) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await poolMemberPaymentsService.delete(id);
      toast.success('Ödeme silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  const openHistory = (memberId: string) => { setHistoryMemberId(memberId); setShowHistoryModal(true); };
  const historyPayments = payments.filter(p => p.poolMemberId === historyMemberId);
  const activeMember = members.find(m => m.id === activePoolMemberId);

  if (!canManage) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500">Bu sayfaya erişim yetkiniz yok.</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Havuz Üyesi Ödemeleri</h1>
        <p className="text-sm text-gray-500 mt-1">Havuz üyelerine yapılan ödemeleri yönetin</p>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Üye</label>
          <select value={selectedMemberId} onChange={e => setSelectedMemberId(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm dark:text-white min-w-[180px]">
            <option value="">Tüm Üyeler</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.fullName}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Başlangıç</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm dark:text-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Bitiş</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm dark:text-white" />
        </div>
      </div>

      {/* Özet Kartlar */}
      {loading ? <p className="text-center text-gray-500 py-12">Yükleniyor...</p> : (
        <div className="space-y-4 mb-8">
          {summary.map(s => (
            <div key={s.poolMemberId} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-100 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{s.poolMemberName}</h3>
                  <p className="text-sm text-gray-500">{s.phone} · {s.transferCount} transfer</p>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Toplam</p>
                    <p className="font-bold text-gray-900 dark:text-white">{Number(s.totalAmount).toFixed(2)} TL</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Ödenen</p>
                    <p className="font-bold text-green-600">{Number(s.totalPaid).toFixed(2)} TL</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Kalan</p>
                    <p className={`font-bold ${Number(s.totalPending) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {Number(s.totalPending).toFixed(2)} TL
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openHistory(s.poolMemberId)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                    Geçmiş
                  </button>
                  {Number(s.totalPending) > 0 && (
                    <button onClick={() => openAddModal(s.poolMemberId)}
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
                      Ödeme Ekle
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {summary.length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500">Kayıt bulunamadı</p>
            </div>
          )}
        </div>
      )}

      {/* Ödeme Ekleme Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Ödeme Ekle — {activeMember?.fullName}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleAddPayment} className="p-4 space-y-4">
              {unpaidTransfers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transfer (Opsiyonel)</label>
                  <select value={selectedTransferForPayment} onChange={e => {
                    setSelectedTransferForPayment(e.target.value);
                    const t = unpaidTransfers.find(t => t.id === e.target.value);
                    if (t) setPaymentAmount(t.remaining.toString());
                  }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white text-sm">
                    <option value="">Genel ödeme</option>
                    {unpaidTransfers.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.transferCode} — {t.pickupLocation?.substring(0, 20)}... ({t.remaining.toFixed(0)} TL kalan)
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tutar (TL) *</label>
                <input type="number" step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="0.00" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Tarihi *</label>
                <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Yöntemi</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                  <option value="cash">Nakit</option>
                  <option value="bank_transfer">Banka Transferi</option>
                  <option value="check">Çek</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Not</label>
                <textarea value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" rows={2} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300 font-medium">İptal</button>
                <button type="submit"
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Geçmiş Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Ödeme Geçmişi — {members.find(m => m.id === historyMemberId)?.fullName}
              </h2>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="p-4">
              {historyPayments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Henüz ödeme yok</p>
              ) : (
                <div className="space-y-3">
                  {historyPayments.map(p => (
                    <div key={p.id} className="flex justify-between items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{Number(p.amount).toFixed(2)} TL</p>
                        <p className="text-sm text-gray-500">{p.paymentDate} · {p.paymentMethod === 'cash' ? 'Nakit' : p.paymentMethod === 'bank_transfer' ? 'Banka' : p.paymentMethod}</p>
                        {p.transfer && <p className="text-xs text-gray-400">{p.transfer.transferCode}</p>}
                        {p.notes && <p className="text-xs text-gray-400">{p.notes}</p>}
                      </div>
                      <button onClick={() => handleDeletePayment(p.id)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium">Sil</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPoolMemberPaymentsPage;
